import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getMembers, getMyProfile, updateGender, getGenderStats } from '@/api/members';
import { updateMember } from '@/api/admin';
import { reclassifyAmbiguousGenders } from '@/lib/gender';
import type { Member } from '@/types/member';
import { toast } from 'sonner';

export function useMembers() {
  return useQuery<Member[]>({
    queryKey: ['members'],
    queryFn: async () => {
      const data = await getMembers();
      // Normalize names to Title Case and reclassify ambiguous genders
      const normalized = data.map((r) => ({
        ...r,
        Name: r.Name
          ? r.Name.trim()
              .split(/\s+/)
              .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
              .join(' ')
          : r.Name,
      }));
      return reclassifyAmbiguousGenders(normalized);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useMyProfile() {
  return useQuery<Member>({
    queryKey: ['my-profile'],
    queryFn: getMyProfile,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateGender() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name, gender }: { name: string; gender: string }) =>
      updateGender(name, gender),
    onMutate: async ({ name, gender }) => {
      await queryClient.cancelQueries({ queryKey: ['members'] });
      const previous = queryClient.getQueryData<Member[]>(['members']);
      queryClient.setQueryData<Member[]>(['members'], (old) =>
        old?.map((m) =>
          m.Name === name ? { ...m, Gender: gender, GenderConfidence: 'manual' } : m
        )
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['members'], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] });
    },
  });
}

export function useGenderStats(cohort?: string) {
  return useQuery({
    queryKey: ['gender-stats', cohort || ''],
    queryFn: () => getGenderStats(cohort || undefined),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      originalName,
      fields,
    }: {
      originalName: string;
      fields: Record<string, string>;
    }) => updateMember(originalName, fields),
    onSuccess: () => {
      toast.success('Profile updated');
      queryClient.invalidateQueries({ queryKey: ['members'] });
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to update member');
    },
  });
}
